import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MonitorSource, SourceHealth, SourceOverview } from "@shared/types";
import { MONITOR_ENVIRONMENT_LABELS } from "@shared/types";
import { ApiError } from "../../lib/api";
import {
  checkSource,
  createSource,
  deleteSource,
  getOverview,
  prettyPhone,
  relativeTime,
  updateSource,
  type SourceInput,
} from "../../lib/monitoring";
import { useToastStore } from "../../stores/toast";
import { Button } from "../../components/ui/button";
import { Modal } from "../../components/ui/modal";
import { Field, Input, Select, Textarea } from "../../components/ui/input";
import { EnvBadge, HealthBadge } from "../../components/supervision/badges";

const EMPTY: SourceInput = {
  clientName: "",
  clientSlug: "",
  environment: "preprod",
  baseUrl: "",
  token: "",
  phoneNumber: null,
  testNumbers: "",
  enabled: true,
  position: 0,
};

function slugOf(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Sources de supervision : un client × un environnement, avec l'adresse de
 * son portail et le jeton généré là-bas. La préproduction se déclare avant la
 * production ; une source se teste avant d'être utilisée.
 */
export function SupervisionSourcesPage() {
  const push = useToastStore((s) => s.push);
  const [items, setItems] = useState<SourceOverview[] | null>(null);
  const [editing, setEditing] = useState<{ source: MonitorSource | null; draft: SourceInput } | null>(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [checks, setChecks] = useState<Record<string, SourceHealth>>({});

  const load = useCallback(() => {
    getOverview()
      .then(setItems)
      .catch(() => {
        setItems([]);
        push("danger", "Sources illisibles");
      });
  }, [push]);
  useEffect(load, [load]);

  const openNew = () => setEditing({ source: null, draft: { ...EMPTY } });
  const openEdit = (s: MonitorSource) =>
    setEditing({
      source: s,
      draft: {
        clientName: s.clientName,
        clientSlug: s.clientSlug,
        environment: s.environment,
        baseUrl: s.baseUrl,
        token: "",
        phoneNumber: s.phoneNumber,
        testNumbers: s.testNumbers.join(", "),
        enabled: s.enabled,
        position: s.position,
      },
    });

  const set = <K extends keyof SourceInput>(key: K, value: SourceInput[K]) =>
    setEditing((e) => (e ? { ...e, draft: { ...e.draft, [key]: value } } : e));

  const save = async () => {
    if (!editing || busy) return;
    setBusy(true);
    try {
      const d = editing.draft;
      const payload = { ...d, phoneNumber: d.phoneNumber?.trim() || null };
      if (editing.source) {
        await updateSource(editing.source.id, payload);
        push("success", "Source mise à jour");
      } else {
        await createSource(payload);
        push("success", "Source ajoutée", "Testez la connexion avant de vous y fier.");
      }
      setEditing(null);
      load();
    } catch (err) {
      const detail =
        err instanceof ApiError && err.status === 400
          ? "Vérifiez l'adresse (origine du portail, sans /api) et le jeton."
          : undefined;
      push("danger", "Enregistrement impossible", detail);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s: MonitorSource) => {
    if (busy) return;
    if (!window.confirm(`Retirer la source ${s.clientName} (${MONITOR_ENVIRONMENT_LABELS[s.environment]}) ? Aucune donnée client n'est touchée.`)) return;
    setBusy(true);
    try {
      await deleteSource(s.id);
      push("success", "Source retirée");
      load();
    } catch {
      push("danger", "Suppression impossible");
    } finally {
      setBusy(false);
    }
  };

  const test = async (s: MonitorSource) => {
    setTesting(s.id);
    try {
      const health = await checkSource(s.id);
      setChecks((c) => ({ ...c, [s.id]: health }));
      if (health.state === "ok") {
        push(
          health.environmentMismatch ? "warning" : "success",
          health.environmentMismatch ? "Joignable, mais environnement différent" : "Connexion réussie",
          health.environmentMismatch
            ? `L'instance se déclare « ${health.remote ? MONITOR_ENVIRONMENT_LABELS[health.remote.environment] : "?"} ».`
            : `${health.stats?.total ?? 0} conversation(s) chez le client, ${health.latencyMs} ms.`,
        );
      } else {
        push("danger", "Connexion impossible", health.message ?? undefined);
      }
    } catch {
      push("danger", "Test impossible");
    } finally {
      setTesting(null);
    }
  };

  return (
    <div className="supervision sv-sources">
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="page-header__title">Sources de supervision</h1>
          <p className="page-header__subtitle">
            Un client, un environnement, l'adresse de son portail et le jeton généré dans ses réglages
            « Répondeur IA ». Déclarez la préproduction avant la production. <Link to="/supervision">Voir les conversations</Link>.
          </p>
        </div>
        <div className="page-header__actions">
          <Button variant="primary" onClick={openNew}>
            Nouvelle source
          </Button>
        </div>
      </header>

      <table className="table">
        <thead>
          <tr>
            <th>Client</th>
            <th>Environnement</th>
            <th>Portail</th>
            <th>Ligne</th>
            <th>Jeton</th>
            <th>État</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items === null ? (
            <tr>
              <td colSpan={7} className="table__empty">
                Chargement…
              </td>
            </tr>
          ) : items.length === 0 ? (
            <tr>
              <td colSpan={7} className="table__empty">
                Aucune source. Ajoutez d'abord la préproduction d'un client.
              </td>
            </tr>
          ) : (
            items.map(({ source: s, health }) => {
              const h = checks[s.id] ?? health;
              return (
                <tr key={s.id} className={s.enabled ? "" : "sv-sources__row--off"}>
                  <td>
                    {s.clientName}
                    {!s.enabled ? <span className="sv-dim"> · désactivée</span> : null}
                  </td>
                  <td>
                    <EnvBadge environment={s.environment} />
                  </td>
                  <td>
                    <span className="sv-code">{s.baseUrl.replace(/^https?:\/\//, "")}</span>
                  </td>
                  <td>{s.phoneNumber ? prettyPhone(s.phoneNumber) : "—"}</td>
                  <td>
                    <span className="sv-code">…{s.tokenHint}</span>
                  </td>
                  <td>
                    <span className="sv-sources__health">
                      <HealthBadge state={h.state} />
                      <span className="sv-dim">
                        {h.state === "ok"
                          ? h.environmentMismatch
                            ? `déclare « ${h.remote ? MONITOR_ENVIRONMENT_LABELS[h.remote.environment] : "?"} »`
                            : `${h.latencyMs} ms · ${h.stats?.lastCallAt ? `dernier appel ${relativeTime(h.stats.lastCallAt)}` : "aucun appel"}`
                          : h.message}
                      </span>
                    </span>
                  </td>
                  <td className="table__actions">
                    <button type="button" className="sv-link" onClick={() => void test(s)} disabled={testing === s.id}>
                      {testing === s.id ? "Test…" : "Tester"}
                    </button>
                    <button type="button" className="sv-link" onClick={() => openEdit(s)}>
                      Modifier
                    </button>
                    <button type="button" className="sv-link sv-link--danger" onClick={() => void remove(s)} disabled={busy}>
                      Retirer
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <Modal
        open={editing !== null}
        title={editing?.source ? "Modifier la source" : "Nouvelle source"}
        onClose={() => setEditing(null)}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={busy}>
              Annuler
            </Button>
            <Button
              variant="primary"
              onClick={() => void save()}
              disabled={
                busy ||
                !editing ||
                !editing.draft.clientName.trim() ||
                !editing.draft.baseUrl.trim() ||
                (!editing.source && !editing.draft.token.trim())
              }
            >
              {editing?.source ? "Enregistrer" : "Ajouter"}
            </Button>
          </>
        }
      >
        {editing ? (
          <div className="sv-form">
            <div className="sv-form__row">
              <Field label="Client" hint="Nom affiché.">
                <Input
                  value={editing.draft.clientName}
                  onChange={(e) => {
                    const name = e.target.value;
                    set("clientName", name);
                    if (!editing.source) set("clientSlug", slugOf(name));
                  }}
                  placeholder="Protecto"
                />
              </Field>
              <Field label="Identifiant" hint="Regroupe les environnements d'un même client.">
                <Input value={editing.draft.clientSlug} onChange={(e) => set("clientSlug", e.target.value)} placeholder="protecto" />
              </Field>
            </div>
            <div className="sv-form__row">
              <Field label="Environnement">
                <Select value={editing.draft.environment} onChange={(e) => set("environment", e.target.value as "prod" | "preprod")}>
                  <option value="preprod">Préproduction · tests</option>
                  <option value="prod">Production · clients réels</option>
                </Select>
              </Field>
              <Field label="Ligne téléphonique" hint="Indicatif, affiché sur la carte.">
                <Input
                  type="tel"
                  value={editing.draft.phoneNumber ?? ""}
                  onChange={(e) => set("phoneNumber", e.target.value)}
                  placeholder="+1 579 999 8485"
                />
              </Field>
            </div>
            <Field label="Adresse du portail" hint="Origine seulement, sans chemin : MC ajoute /api/appels/monitoring.">
              <Input
                type="url"
                value={editing.draft.baseUrl}
                onChange={(e) => set("baseUrl", e.target.value)}
                placeholder="https://app.protecto.umbeli.com"
              />
            </Field>
            <Field
              label={editing.source ? "Nouveau jeton (facultatif)" : "Jeton de supervision"}
              hint={
                editing.source
                  ? `Jeton actuel …${editing.source.tokenHint}. Laissez vide pour le conserver.`
                  : "Généré dans le portail du client : Répondeur IA → Supervision Umbeli."
              }
            >
              <Input
                value={editing.draft.token}
                onChange={(e) => set("token", e.target.value)}
                placeholder="pmon_…"
                autoComplete="off"
                spellCheck={false}
              />
            </Field>
            <Field
              label="Numéros de test internes"
              hint="Séparés par des virgules. Leurs appels sont marqués « test interne », même en production."
            >
              <Textarea
                rows={2}
                value={editing.draft.testNumbers}
                onChange={(e) => set("testNumbers", e.target.value)}
                placeholder="+1 418 555-0100, +1 514 555-0000"
              />
            </Field>
            <div className="sv-form__row">
              <Field label="Ordre" hint="Les cartes se classent par ordre croissant.">
                <Input
                  type="number"
                  min={0}
                  max={1000}
                  value={editing.draft.position}
                  onChange={(e) => set("position", Number(e.target.value) || 0)}
                />
              </Field>
              <Field label="Active">
                <Select value={editing.draft.enabled ? "1" : "0"} onChange={(e) => set("enabled", e.target.value === "1")}>
                  <option value="1">Oui — consultée</option>
                  <option value="0">Non — conservée sans lecture</option>
                </Select>
              </Field>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
